import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import { authService } from '@/services/authService'
import { useToast } from 'vue-toastification'

interface User {
  id: string
  username: string
  email: string
  roles: string[]
  keycloakId: string
  name?: string
}

export const useAuthStore = defineStore('auth', () => {
  const toast = useToast()
  
  // Estado reactivo
  const user = ref<User | null>(null)
  const isLoading = ref(false)
  const error = ref<string | null>(null)
  const hasShownWelcomeToast = ref(false)
  const isInitialized = ref(false)

  // Getters computados
  const isAuthenticated = computed(() => {
    return authService.isAuthenticated() && !!user.value
  })
  const isAdmin = computed(() => {
    return authService.hasAdminAccess() && user.value?.roles?.includes('admin')
  })
  const accessToken = computed(() => authService.getToken())
  const hasRefreshToken = computed(() => !!authService.getToken())

  // Inicializar Keycloak y procesar usuario autenticado
  async function initialize(): Promise<boolean> {
    try {
      isLoading.value = true
      console.log('🔄 Inicializando auth store...')

      // Inicializar Keycloak
      const authenticated = await authService.init()
      isInitialized.value = true

      if (authenticated) {
        await processAuthenticatedUser()
        return true
      }

      return false
    } catch (error) {
      console.error('❌ Error inicializando auth store:', error)
      return false
    } finally {
      isLoading.value = false
    }
  }

  // Procesar usuario autenticado
  async function processAuthenticatedUser(): Promise<void> {
    try {
      const userInfo = authService.getUserInfo()
      
      if (userInfo) {
        console.log('✅ Usuario autenticado:', userInfo.username)
        
        // Verificar rol de administrador
        if (!authService.hasAdminAccess()) {
          console.error('❌ Usuario sin permisos de administrador')
          toast.error('Acceso denegado: se requiere rol de administrador')
          await logout()
          return
        }

        // Configurar usuario en el store
        user.value = {
          id: userInfo.id,
          username: userInfo.username,
          email: userInfo.email,
          roles: userInfo.roles,
          keycloakId: userInfo.keycloakId,
          name: userInfo.name
        }

        // Mostrar toast de bienvenida solo una vez
        if (!hasShownWelcomeToast.value) {
          toast.success(`Bienvenido, ${user.value.username}!`)
          hasShownWelcomeToast.value = true
        }
      }
    } catch (error) {
      console.error('❌ Error procesando usuario autenticado:', error)
      toast.error('Error procesando información del usuario')
    }
  }

  // Iniciar sesión con redirección a Keycloak
  async function login(): Promise<void> {
    try {
      isLoading.value = true
      console.log('🔑 Iniciando login...')
      
      if (!isInitialized.value) {
        await authService.init()
        isInitialized.value = true
      }

      // Redirigir a Keycloak para login
      await authService.login()
    } catch (error: any) {
      console.error('❌ Error durante login:', error)
      toast.error(error.message || 'Error durante el login')
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Procesar callback de autenticación de Keycloak
   */
  async function processCallback(): Promise<boolean> {
    try {
      console.log('🔄 Procesando callback de autenticación...')
      
      // Verificar si hay parámetros de error en la URL
      const urlParams = new URLSearchParams(window.location.search)
      if (urlParams.has('error')) {
        const error = urlParams.get('error')
        const errorDescription = urlParams.get('error_description')
        console.error('❌ Error en callback de Keycloak:', error, errorDescription)
        
        toast.error(`Error de autenticación: ${errorDescription || error}`, {
          timeout: 5000,
        })
        
        return false
      }
      
      const success = await authService.processAuthenticationCallback()
      
      if (success) {
        await processAuthenticatedUser()
        
        toast.success('Autenticación exitosa', {
          timeout: 3000,
        })
        
        return true
      }
      
      console.log('❌ Callback procesado pero autenticación fallida')
      return false
    } catch (error: any) {
      console.error('❌ Error procesando callback:', error)
      
      toast.error('Error en la autenticación', {
        timeout: 5000,
      })
      
      return false
    }
  }

  // Cerrar sesión
  async function logout(silent: boolean = false): Promise<void> {
    try {
      if (isLoading.value) {
        return
      }
      
      isLoading.value = true
      console.log('🚪 Cerrando sesión...')

      // Limpiar estado local primero
      user.value = null
      hasShownWelcomeToast.value = false

      // Logout en Keycloak (redirige)
      await authService.logout()

      if (!silent) {
        toast.info('Sesión cerrada exitosamente')
      }
    } catch (error) {
      console.error('❌ Error durante logout:', error)
      // Limpiar estado local aunque falle el logout remoto
      user.value = null
      
      if (!silent) {
        toast.info('Sesión cerrada localmente')
      }
    } finally {
      isLoading.value = false
    }
  }

  // Renovar token
  async function refreshToken(): Promise<boolean> {
    try {
      if (!isInitialized.value) {
        return false
      }

      const success = await authService.updateToken()
      
      if (success) {
        console.log('🔄 Token renovado exitosamente')
        return true
      }

      console.warn('⚠️ No se pudo renovar el token')
      return false
    } catch (error) {
      console.error('❌ Error renovando token:', error)
      return false
    }
  }

  // Verificar autenticación
  async function checkAuth(): Promise<boolean> {
    try {
      if (!isInitialized.value) {
        await initialize()
      }

      if (authService.isAuthenticated()) {
        // Si no tenemos usuario en el store pero Keycloak dice que estamos autenticados
        if (!user.value) {
          await processAuthenticatedUser()
        }
        return true
      }

      return false
    } catch (error) {
      console.error('❌ Error verificando autenticación:', error)
      return false
    }
  }

  // Obtener headers de autorización
  function getAuthHeaders(): Record<string, string> {
    return authService.getAuthHeaders()
  }

  return {
    // Estado
    user,
    isLoading,
    isInitialized,
    
    // Getters
    isAuthenticated,
    isAdmin,
    accessToken,
    hasRefreshToken,
    
    // Acciones
    initialize,
    login,
    logout,
    processCallback,
    refreshToken,
    checkAuth,
    getAuthHeaders,
    processAuthenticatedUser
  }
})
